import { useEffect, useState } from "react";
import { money0 } from "../lib/format";
import { listSyndicates } from "../lib/syndicate";
import {
  COMPACT_VERSION, COMPACT_DOS, COMPACT_DONTS, PLEDGE_TEXT, DISCLAIMERS, POST_CATEGORIES,
  hasAcceptedCompact, acceptedName, acceptCompact,
  listPosts, addPost, newPostId, listLeads, addLead, newLeadId,
  listRequests, addRequest, voteRequest, hasVoted, newRequestId, seedStarterRequests,
  type CommunityPost, type CommunityLead, type CommunityRequest, type RequestKind,
} from "../lib/community";

type Tab = "knowledge" | "leads" | "requests" | "compact";

export function Community({ initialTab }: { initialTab?: Tab }) {
  const [accepted, setAccepted] = useState(hasAcceptedCompact());
  const [tab, setTab] = useState<Tab>(initialTab ?? "knowledge");
  const [groups, setGroups] = useState<string[]>([]);

  useEffect(() => { listSyndicates().then((s) => setGroups(s.map((x) => x.name))).catch(() => {}); }, []);

  if (!accepted) return <CompactGate onAccept={(name) => { void acceptCompact(name); setAccepted(true); }} />;

  return (
    <div className="wrap">
      <div className="pe">
        <div className="l">◆ Community — learn together, share leads, stay compliant</div>
        <p>
          A network of investor groups sharing <b>best practices, pitfalls, playbooks and market insight</b> — and property
          <b> leads</b> that fall outside one group's box. <b>Knowledge and leads only:</b> no soliciting each other's
          investors, no commissions, no advice. Beacon holds no funds and facilitates no transactions. You signed the
          Compact — that standard is non-negotiable.
        </p>
      </div>

      <div className="crm-tabs">
        <button className={tab === "knowledge" ? "on" : ""} onClick={() => setTab("knowledge")}>💡 Knowledge</button>
        <button className={tab === "leads" ? "on" : ""} onClick={() => setTab("leads")}>🏷️ Deal leads</button>
        <button className={tab === "requests" ? "on" : ""} onClick={() => setTab("requests")}>📋 Requests</button>
        <button className={tab === "compact" ? "on" : ""} onClick={() => setTab("compact")}>🛡️ The Compact</button>
      </div>

      {tab === "knowledge" ? <Knowledge groups={groups} /> : tab === "leads" ? <Leads groups={groups} /> : tab === "requests" ? <Requests /> : <CompactView />}
    </div>
  );
}

// --- the signed-pledge gate -------------------------------------------------
function CompactGate({ onAccept }: { onAccept: (name: string) => void }) {
  const [name, setName] = useState("");
  const ok = name.trim().length >= 2;
  return (
    <div className="wrap" style={{ maxWidth: 760 }}>
      <div className="card pad" style={{ borderLeft: "4px solid var(--brass)" }}>
        <div className="eyebrow">Community Compact · v{COMPACT_VERSION}</div>
        <h2 className="fr" style={{ color: "var(--forest)", fontSize: 26, margin: "6px 0 4px" }}>Before you enter — our commitment to 100% compliance</h2>
        <p style={{ fontSize: 14, color: "#37413c", lineHeight: 1.6, marginBottom: 16 }}>
          This community exists to help investor groups <b>learn together and share leads</b> — safely and legally.
          Everyone signs the same commitment. Read it, then sign to enter.
        </p>

        <div className="grid g2" style={{ marginBottom: 14 }}>
          <div className="card pad" style={{ background: "rgba(62,122,94,.06)", border: "1px solid rgba(62,122,94,.25)" }}>
            <div className="eyebrow" style={{ color: "var(--pass)" }}>✓ We do</div>
            <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13, color: "#2b332f", lineHeight: 1.55 }}>
              {COMPACT_DOS.map((d, i) => <li key={i} style={{ marginBottom: 6 }}>{d}</li>)}
            </ul>
          </div>
          <div className="card pad" style={{ background: "rgba(166,80,60,.05)", border: "1px solid rgba(166,80,60,.28)" }}>
            <div className="eyebrow" style={{ color: "var(--fail)" }}>✕ We never</div>
            <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13, color: "#3a2b28", lineHeight: 1.55 }}>
              {COMPACT_DONTS.map((d, i) => <li key={i} style={{ marginBottom: 6 }}>{d}</li>)}
            </ul>
          </div>
        </div>

        <div className="card pad" style={{ background: "var(--cream)", margin: "0 0 14px" }}>
          <div className="eyebrow">Your pledge</div>
          <p style={{ fontSize: 13.5, color: "var(--forest)", lineHeight: 1.6, marginTop: 6, fontStyle: "italic" }}>“{PLEDGE_TEXT}”</p>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input className="bb-name" style={{ minWidth: 240 }} placeholder="Type your full name to sign" value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn btn-p" onClick={() => ok && onAccept(name.trim())} disabled={!ok}>✍ I agree &amp; sign the Compact</button>
        </div>
        <div className="note" style={{ marginTop: 10 }}>{DISCLAIMERS.footer}</div>
      </div>
    </div>
  );
}

function CompactView() {
  return (
    <>
      <div className="flag ok" style={{ marginBottom: 12 }}><span>✓</span><div>You signed the Community Compact v{COMPACT_VERSION}{acceptedName() ? ` as ${acceptedName()}` : ""}. Thank you for keeping this community clean.</div></div>
      <div className="grid g2">
        <div className="card pad">
          <div className="eyebrow" style={{ color: "var(--pass)" }}>✓ We do</div>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13, color: "#2b332f", lineHeight: 1.6 }}>{COMPACT_DOS.map((d, i) => <li key={i} style={{ marginBottom: 6 }}>{d}</li>)}</ul>
        </div>
        <div className="card pad">
          <div className="eyebrow" style={{ color: "var(--fail)" }}>✕ We never</div>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13, color: "#3a2b28", lineHeight: 1.6 }}>{COMPACT_DONTS.map((d, i) => <li key={i} style={{ marginBottom: 6 }}>{d}</li>)}</ul>
        </div>
      </div>
      <div className="card pad" style={{ marginTop: 12, background: "var(--cream)" }}>
        <div className="eyebrow">The pledge you signed</div>
        <p style={{ fontSize: 13.5, color: "var(--forest)", lineHeight: 1.6, marginTop: 6, fontStyle: "italic" }}>“{PLEDGE_TEXT}”</p>
      </div>
      <div className="note" style={{ marginTop: 10 }}>{DISCLAIMERS.footer}</div>
    </>
  );
}

// --- knowledge feed ---------------------------------------------------------
function Knowledge({ groups }: { groups: string[] }) {
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [group, setGroup] = useState("");
  const [category, setCategory] = useState<string>(POST_CATEGORIES[0]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => { listPosts().then(setPosts).catch(() => {}); if (groups[0]) setGroup(groups[0]); }, [groups.length]);

  async function submit() {
    if (!group.trim() || !title.trim() || !body.trim()) return;
    await addPost({ id: newPostId(), group: group.trim(), author: group.trim(), category, title: title.trim(), body: body.trim() });
    setTitle(""); setBody(""); setPosts(await listPosts());
  }

  return (
    <>
      <div className="flag warn" style={{ marginBottom: 12 }}><span>◐</span><div>{DISCLAIMERS.knowledge}</div></div>
      <div className="card pad" style={{ marginBottom: 14 }}>
        <div className="eyebrow">Share something the community can learn from</div>
        <div className="bb-grid" style={{ marginTop: 10 }}>
          <label className="bb-f"><span>Posting as (group)</span><input list="comm-groups" value={group} onChange={(e) => setGroup(e.target.value)} placeholder="your group name" /><datalist id="comm-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist></label>
          <label className="bb-f"><span>Category</span><select value={category} onChange={(e) => setCategory(e.target.value)}>{POST_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
        </div>
        <label className="bb-f" style={{ marginTop: 10 }}><span>Title</span><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. The insurance mistake that cost us a deal" /></label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="What happened, what you learned, what others should do differently…"
          style={{ width: "100%", marginTop: 10, padding: 12, border: "1px solid var(--line)", borderRadius: 9, fontSize: 13.5, fontFamily: "inherit", background: "var(--white)", resize: "vertical" }} />
        <button className="btn btn-p btn-sm" style={{ marginTop: 10 }} onClick={submit} disabled={!group.trim() || !title.trim() || !body.trim()}>Post to community</button>
      </div>

      {posts.length === 0 ? (
        <div className="note">No posts yet. Be the first to share a lesson — the community grows on what everyone brings.</div>
      ) : posts.map((p) => (
        <div key={p.id} className="card pad" style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 700, color: "var(--forest)", fontSize: 15.5 }}>{p.title}</div>
            <span className="tag t-watch">{p.category}</span>
          </div>
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 2 }}>{p.group}{p.created_at ? ` · ${p.created_at.slice(0, 10)}` : ""}</div>
          <p style={{ fontSize: 13.5, color: "#37413c", marginTop: 8, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{p.body}</p>
        </div>
      ))}
    </>
  );
}

// --- requests (course / feature voting → the roadmap) -----------------------
const STATUS_META: Record<string, { cls: string; label: string }> = {
  open: { cls: "t-watch", label: "Open" }, planned: { cls: "t-pass", label: "Planned" }, shipped: { cls: "t-pass", label: "Shipped ✓" },
};
function Requests() {
  const [reqs, setReqs] = useState<CommunityRequest[]>([]);
  const [kind, setKind] = useState<RequestKind>("course");
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [nudge, setNudge] = useState(0);

  useEffect(() => { seedStarterRequests().then(listRequests).then(setReqs).catch(() => {}); }, []);

  async function submit() {
    if (!title.trim()) return;
    await addRequest({ id: newRequestId(), kind, title: title.trim(), detail: detail.trim() || undefined, votes: 1, status: "open" });
    setTitle(""); setDetail(""); setReqs(await listRequests());
  }
  async function vote(id: string) {
    const n = await voteRequest(id);
    if (n != null) { setReqs(await listRequests()); setNudge((x) => x + 1); }
  }

  return (
    <>
      <div className="flag ok" style={{ marginBottom: 12 }}><span>◆</span><div>Tell us what to build. The community <b>votes courses and features up</b>, and the top ones get built right here. Your voice shapes the roadmap.</div></div>
      <div className="card pad" style={{ marginBottom: 14 }}>
        <div className="eyebrow">Request a course or feature</div>
        <div className="bb-grid" style={{ marginTop: 10 }}>
          <label className="bb-f"><span>Type</span><select value={kind} onChange={(e) => setKind(e.target.value as RequestKind)}><option value="course">📚 Course</option><option value="feature">⚙️ Feature</option></select></label>
          <label className="bb-f" style={{ gridColumn: "span 3" }}><span>Title</span><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kind === "course" ? "e.g. Tenant screening under Fair Housing" : "e.g. Export the cap table to Excel"} /></label>
        </div>
        <label className="bb-f" style={{ marginTop: 10 }}><span>Details (optional)</span><input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="what would make this valuable to you" /></label>
        <button className="btn btn-p btn-sm" style={{ marginTop: 10 }} onClick={submit} disabled={!title.trim()}>Submit request</button>
      </div>

      {reqs.length === 0 ? (
        <div className="note">No requests yet. Be the first — ask for the course or feature you want most.</div>
      ) : reqs.map((r) => {
        const voted = hasVoted(r.id);
        const meta = STATUS_META[r.status] || STATUS_META.open;
        return (
          <div key={r.id + nudge} className="card pad" style={{ marginBottom: 10, display: "flex", gap: 14, alignItems: "flex-start" }}>
            <button className={"vote-btn" + (voted ? " voted" : "")} onClick={() => vote(r.id)} disabled={voted} title={voted ? "You voted" : "Upvote"}>
              <span style={{ fontSize: 14 }}>▲</span><span style={{ fontWeight: 700 }}>{r.votes}</span>
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span className="tag t-watch">{r.kind === "course" ? "📚 Course" : "⚙️ Feature"}</span>
                <span className={"tag " + meta.cls}>{meta.label}</span>
              </div>
              <div style={{ fontWeight: 700, color: "var(--forest)", fontSize: 15, marginTop: 6 }}>{r.title}</div>
              {r.detail && <p style={{ fontSize: 13, color: "#37413c", marginTop: 4, lineHeight: 1.55 }}>{r.detail}</p>}
            </div>
          </div>
        );
      })}
    </>
  );
}

// --- deal leads (property leads only, NO commissions) -----------------------
function Leads({ groups }: { groups: string[] }) {
  const [leads, setLeads] = useState<CommunityLead[]>([]);
  const [group, setGroup] = useState("");
  const [market, setMarket] = useState("");
  const [address, setAddress] = useState("");
  const [ask, setAsk] = useState("");
  const [note, setNote] = useState("");
  const [contact, setContact] = useState("");

  useEffect(() => { listLeads().then(setLeads).catch(() => {}); if (groups[0]) setGroup(groups[0]); }, [groups.length]);

  async function submit() {
    if (!group.trim() || !market.trim() || !note.trim()) return;
    await addLead({ id: newLeadId(), group: group.trim(), author: group.trim(), market: market.trim(), address: address.trim() || undefined, ask: Number(ask.replace(/[^0-9.]/g, "")) || undefined, note: note.trim(), contact: contact.trim() || undefined });
    setAddress(""); setAsk(""); setNote(""); setLeads(await listLeads());
  }

  return (
    <>
      <div className="flag warn" style={{ marginBottom: 12 }}><span>◐</span><div><b>Leads only.</b> {DISCLAIMERS.leads}</div></div>
      <div className="card pad" style={{ marginBottom: 14 }}>
        <div className="eyebrow">Pass a lead you can't take — help another group win it</div>
        <div className="bb-grid" style={{ marginTop: 10 }}>
          <label className="bb-f"><span>Posting as (group)</span><input list="comm-groups2" value={group} onChange={(e) => setGroup(e.target.value)} placeholder="your group name" /><datalist id="comm-groups2">{groups.map((g) => <option key={g} value={g} />)}</datalist></label>
          <label className="bb-f"><span>Market</span><input value={market} onChange={(e) => setMarket(e.target.value)} placeholder="e.g. Jacksonville, FL" /></label>
          <label className="bb-f"><span>Address (optional)</span><input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="street address" /></label>
          <label className="bb-f"><span>Asking (optional)</span><input type="number" value={ask} onChange={(e) => setAsk(e.target.value)} placeholder="0" /></label>
        </div>
        <label className="bb-f" style={{ marginTop: 10 }}><span>Why you're passing / the opportunity</span><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. great numbers but outside our market — needs a $30k roof" /></label>
        <label className="bb-f" style={{ marginTop: 10 }}><span>How to reach you (off-platform)</span><input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="email or phone — you handle it directly" /></label>
        <button className="btn btn-p btn-sm" style={{ marginTop: 10 }} onClick={submit} disabled={!group.trim() || !market.trim() || !note.trim()}>Post lead</button>
        <div className="note" style={{ marginTop: 8 }}>No commissions are paid or accepted on Beacon. If you connect, you handle everything directly with your own counsel. This is a lead, not an offering — do your own diligence.</div>
      </div>

      {leads.length === 0 ? (
        <div className="note">No leads yet. Share a deal that's outside your box — another group might be perfect for it.</div>
      ) : leads.map((l) => (
        <div key={l.id} className="card pad" style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 700, color: "var(--forest)", fontSize: 15 }}>{l.market}{l.address ? ` · ${l.address}` : ""}</div>
            {l.ask ? <span className="tag t-watch">{money0(l.ask)}</span> : null}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 2 }}>from {l.group}{l.created_at ? ` · ${l.created_at.slice(0, 10)}` : ""}</div>
          <p style={{ fontSize: 13.5, color: "#37413c", marginTop: 8, lineHeight: 1.6 }}>{l.note}</p>
          {l.contact && <div className="flag ok" style={{ marginTop: 8 }}><span>◆</span><div>Interested? Reach out directly: <b>{l.contact}</b> — handle it off-platform, with your own diligence &amp; counsel.</div></div>}
        </div>
      ))}
    </>
  );
}
